// manufacturerDataHealth.ts
//
// Pure, read-only helpers backing the Phase 2 "Data Health" audit UI for movement
// manufacturers. Everything here is derived from fields already stored on the
// manufacturer doc — `brandIdsManualInclude` (hand-curated), `brandIdsDerivedFromRefs`
// (auto-populated by the deriveManufacturerBrands callable in Phase 1b),
// `brandExclusions`, `primaryBrandId` and `isGeneric`. No Firestore reads, no ref
// scans, no writes. The audit exists so a human can eyeball derivation quality
// before Phase 3 makes the derived set authoritative.
//
// NOTE on ref counts: the derived set is a deduped, alphabetically-sorted array
// with NO per-brand frequency information (see deriveManufacturerBrands, which
// stores `sortedUnique([...set])`). "Most common brand in the derived set" is
// therefore not recoverable client-side without re-scanning the whole references
// collection group — too heavy for a page-load audit. `suggestedPrimaryBrandId`
// below uses a deterministic heuristic (self-brand → stored-if-valid →
// first-of-effective-alphabetical), suppressed entirely for generic / very-broad
// manufacturers, and callers surface that caveat in the UI.

import type { MovementManufacturer } from '../../types';

/** Suggested generic classification derived from the effective brand count. */
export type GenericClassification = 'generic' | 'semi-generic' | 'non-generic';

export interface ManufacturerDataHealth {
  /** Hand-curated allowlist (`brandIdsManualInclude`), sorted-unique. */
  manual: string[];
  /** Auto-derived-from-refs set (`brandIdsDerivedFromRefs`), sorted-unique. */
  derived: string[];
  /** Explicit exclusions (`brandExclusions`), sorted-unique. */
  exclusions: string[];
  /**
   * Effective brand set = union(manual, derived) minus exclusions. This is what
   * consumers will dual-read in Phase 4; drives the isGeneric signal here.
   */
  effective: string[];
  /**
   * Brands present in the derived set but NOT in the manual allowlist (and not
   * excluded) — refs use this manufacturer's calibres for these brands but they
   * were never hand-curated. Candidates to promote in Phase 3.
   */
  newInDerived: string[];
  /**
   * Brands hand-linked in the manual allowlist but absent from the derived set
   * (and not excluded) — no ref actually uses this manufacturer's calibres for
   * them. Possibly a stale manual link.
   */
  staleInManual: string[];
  /** newInDerived.length + staleInManual.length — the sort key for "biggest diffs first". */
  diffCount: number;
  /** Current stored `primaryBrandId` (or undefined). */
  currentPrimaryBrandId?: string;
  /**
   * Deterministic suggested primary. `undefined` when a primary isn't meaningful
   * (generic manufacturer, or effective set too large — see below). Otherwise,
   * in priority order: the manufacturer's own slug when it is itself a brand in
   * the effective set (e.g. `laco` → `laco`); else the current primary when it
   * is still in the effective set; else the first effective brand (alphabetical).
   * See the file header caveat — this is NOT frequency-ranked.
   */
  suggestedPrimaryBrandId?: string;
  /**
   * True only when there IS a suggestion and it differs from the stored value.
   * When the suggestion is suppressed (generic / too-many-brands) this is false.
   */
  primaryDisagrees: boolean;
  /** Current stored `isGeneric` (undefined = never classified). */
  currentIsGeneric?: boolean;
  /**
   * Suggested classification. Honors an explicit manual `isGeneric` flag as the
   * source of truth (true → generic; false → never generic); falls back to the
   * effective-count thresholds only when the flag is unset. See classifyManufacturer.
   */
  suggestedClassification: GenericClassification;
  /** Boolean form of the suggestion (semi-generic maps to non-generic / false). */
  suggestedIsGeneric: boolean;
  /** Whether the stored isGeneric disagrees with the suggested boolean. */
  isGenericDisagrees: boolean;
}

/** Trim + drop empties + dedupe + sort — the canonical shape the callable writes. */
function sortedUnique(values: readonly string[] | undefined): string[] {
  if (!values || values.length === 0) return [];
  const set = new Set<string>();
  for (const v of values) {
    const t = typeof v === 'string' ? v.trim() : '';
    if (t) set.add(t);
  }
  return [...set].sort();
}

/** a minus b (set difference), preserving a's (already sorted) order. */
function difference(a: readonly string[], b: readonly string[]): string[] {
  const bSet = new Set(b);
  return a.filter((x) => !bSet.has(x));
}

/**
 * Classify a manufacturer by its effective brand count, per the Phase 2 spec:
 *   0     → generic       (no brand uses it — treat as a generic/orphan)
 *   1–2   → non-generic   (exclusive to one/two makers → in-house)
 *   3–8   → semi-generic  (several makers — still classified non-generic)
 *   9+    → generic       (widely used across many brands → generic/third-party)
 */
export function classifyByEffectiveCount(count: number): GenericClassification {
  if (count === 0) return 'generic';
  if (count <= 2) return 'non-generic';
  if (count <= 8) return 'semi-generic';
  return 'generic';
}

/**
 * Suggested classification that treats an explicit manual `isGeneric` flag as the
 * source of truth, falling back to pure count-derivation only when it's unset:
 *
 *   isGeneric === true       → 'generic'  (respect the explicit flag; e.g. ESA is
 *                              hand-marked generic even though its derived count
 *                              of 3–8 would otherwise read "semi-generic").
 *   isGeneric === false       → never 'generic'. The count still picks the tier,
 *                              but the two buckets that derive to 'generic' (0 and
 *                              9+) are clamped to their nearest non-generic tier
 *                              (0 → 'non-generic', 9+ → 'semi-generic') so an
 *                              explicit "not generic" is never overridden back to
 *                              generic by the derivation.
 *   isGeneric === undefined   → pure count-derivation (unchanged legacy behavior).
 */
export function classifyManufacturer(
  count: number,
  isGeneric: boolean | undefined,
): GenericClassification {
  if (isGeneric === true) return 'generic';
  const derived = classifyByEffectiveCount(count);
  if (isGeneric === false && derived === 'generic') {
    // User explicitly said non-generic; keep it out of the generic bucket. Pick
    // the nearest non-generic tier by breadth: empty/none → non-generic, wide → semi.
    return count === 0 ? 'non-generic' : 'semi-generic';
  }
  return derived;
}

/** semi-generic and non-generic both mean "not a generic manufacturer" (false). */
export function classificationToBoolean(c: GenericClassification): boolean {
  return c === 'generic';
}

/**
 * Above this many effective brands a "primary" brand stops being meaningful — a
 * manufacturer spread across this many makers has no single home brand, so we
 * suppress the suggestion rather than pick an arbitrary alphabetical winner.
 * 5 keeps a primary for exclusive / small-set in-house makers (1–5 brands) while
 * hiding it for widely-shared semi-generics (6–8) and generics (9+, already
 * suppressed by the generic check). Judgment call — tune if it reads wrong.
 */
export const HIGH_EFFECTIVE_PRIMARY_THRESHOLD = 5;

/**
 * Compute the full read-only health snapshot for a single manufacturer from its
 * stored fields alone. Pure — safe to call on every render / row.
 */
export function computeManufacturerDataHealth(
  m: Pick<
    MovementManufacturer,
    | 'id'
    | 'brandIdsManualInclude'
    | 'brandIdsDerivedFromRefs'
    | 'brandExclusions'
    | 'primaryBrandId'
    | 'isGeneric'
  >,
): ManufacturerDataHealth {
  const manual = sortedUnique(m.brandIdsManualInclude);
  const derived = sortedUnique(m.brandIdsDerivedFromRefs);
  const exclusions = sortedUnique(m.brandExclusions);
  const exclusionSet = new Set(exclusions);

  const effective = sortedUnique(
    [...manual, ...derived].filter((id) => !exclusionSet.has(id)),
  );

  // Diffs ignore excluded ids (an excluded brand is a resolved decision, not a diff).
  const newInDerived = difference(derived, manual).filter((id) => !exclusionSet.has(id));
  const staleInManual = difference(manual, derived).filter((id) => !exclusionSet.has(id));

  const currentIsGeneric = m.isGeneric;
  const suggestedClassification = classifyManufacturer(effective.length, currentIsGeneric);
  const suggestedIsGeneric = classificationToBoolean(suggestedClassification);
  const isGenericDisagrees =
    currentIsGeneric === undefined || currentIsGeneric !== suggestedIsGeneric;

  const currentPrimaryBrandId = m.primaryBrandId?.trim() || undefined;
  const effectiveSet = new Set(effective);

  // Suppress the suggestion where a single "primary" brand isn't meaningful:
  // an empty/generic manufacturer (no home brand) or one explicitly marked
  // generic, and semi-generics spread across too many makers.
  const suppressPrimary =
    effective.length === 0 ||
    suggestedIsGeneric ||
    currentIsGeneric === true ||
    effective.length > HIGH_EFFECTIVE_PRIMARY_THRESHOLD;

  const manufacturerId = m.id?.trim() || undefined;
  const suggestedPrimaryBrandId = suppressPrimary
    ? undefined
    : // Self-brand preferred: the manufacturer's own slug is itself a brand in
      // the effective set (e.g. `laco` derives brand `laco`).
      manufacturerId && effectiveSet.has(manufacturerId)
      ? manufacturerId
      : // Else keep the stored primary if it still holds up.
        currentPrimaryBrandId && effectiveSet.has(currentPrimaryBrandId)
        ? currentPrimaryBrandId
        : // Else first-of-effective (alphabetical — `effective` is sorted).
          effective[0] ?? undefined;

  // Only a disagreement when there's actually a suggestion to disagree with.
  const primaryDisagrees =
    suggestedPrimaryBrandId !== undefined &&
    suggestedPrimaryBrandId !== currentPrimaryBrandId;

  return {
    manual,
    derived,
    exclusions,
    effective,
    newInDerived,
    staleInManual,
    diffCount: newInDerived.length + staleInManual.length,
    currentPrimaryBrandId,
    suggestedPrimaryBrandId,
    primaryDisagrees,
    currentIsGeneric,
    suggestedClassification,
    suggestedIsGeneric,
    isGenericDisagrees,
  };
}
